<template>
  <div
    ref="map"
    class="interactive-map"
  />
</template>

<script>
  import L from 'leaflet'

  import { currencyFilter } from '@/utils/filters'

  export default {
    name: 'BaseInteractiveMap',

    props: {
      selectedCard: {
        type: String,
        required: true
      },
      indicators: {
        type: Array,
        default: () => []
      },
      formattedIndicators: {
        type: Array,
        default: () => []
      },
      geoJson: {
        type: Object,
        required: true,
        validator: function (value) {
          return value && typeof value === 'object' && value.type === 'FeatureCollection'
        }
      },
      hoveredRegion: String
    },

    data() {
      return {
        map: null,
        geoJsonLayer: null,
        originalGeoJSON: null,
        mapOptions: {
          attributionControl: false,
          zoomControl: false,
          zoomSnap: 0,
          dragging: false,
          touchZoom: false,
          doubleClickZoom: false,
          scrollWheelZoom: false,
          boxZoom: false,
          keyboard: false,
          maxZoom: 18
        },
        tooltipOptions: {
          permanent: true,
          direction: 'center',
          opacity: 1,
          offset: [40, 0]
        }
      }
    },

    watch: {
      selectedCard() {
        this.updateGeoJsonLayer()
      }
    },

    beforeDestroy() {
      this.cleanupMap()
    },

    mounted() {
      this.originalGeoJSON = JSON.parse(JSON.stringify(this.geoJson))
      this.initializeMap()
    },

    methods: {
      getIndicatorValueForRegion(regionCode, useFormatted = true) {
        const indicatorsArray = useFormatted ? this.formattedIndicators : this.indicators
        const indicator = indicatorsArray.find(indicator => indicator.region === regionCode)

        if (this.selectedCard === 'conversions') {
          let conversionsText = indicator.conversions + '\r\n'
          conversionsText += currencyFilter(indicator.total_value_sold)

          return {
            value: indicator.conversions,
            text: conversionsText
          }
        }

        if (this.selectedCard === 'feeling') {
          let feelingText = ''
          if(!indicator.negative_feeling && !indicator.neutral_feeling  && !indicator.positive_feeling ){
            feelingText += 'Negativo: 0%\r\n'
            feelingText += 'Neutro: 0%\r\n'
            feelingText += 'Positivo: 0%'
            return feelingText
          }
          feelingText += 'Negativo: ' + indicator.negative_feeling + '\r\n'
          feelingText += 'Neutro: ' + indicator.neutral_feeling + '\r\n'
          feelingText += 'Positivo: ' + indicator.positive_feeling

          return feelingText
        }

        return indicator ? indicator[this.selectedCard] : null
      },

      highlightFeature(e) {
        const newRegion = e.target.feature.properties.code
        this.$emit('update:hoveredRegion', newRegion)

        let indicatorValue = this.getIndicatorValueForRegion(newRegion, false)

        if (this.selectedCard === 'conversions') {
          indicatorValue = indicatorValue.value
        }

        const maxValue = this.getMaxValue()
        const alpha = this.getAlphaChannel(indicatorValue / maxValue)

        this.setFeatureStyle(e, { fillColor: this.getFillColor(indicatorValue, alpha, true) })

        this.updateOtherLayersOpacity(e, 0.35)
        this.updateTooltipColorBasedOnAlpha(e, alpha)
      },

      isSpecialIndicator() {
        return (
          this.selectedCard === 'reason_refusal' ||
          this.selectedCard === 'best_selling_product' ||
          this.selectedCard === 'feeling'
        )
      },

      updateOtherLayersOpacity(e, opacity) {
        this.geoJsonLayer.eachLayer(layer => {
          if (layer !== e.target) {
            layer.setStyle({ fillOpacity: opacity })

            layer.getTooltip()?.setOpacity(opacity)
          }
        })
      },

      updateTooltipColorBasedOnAlpha(e, alpha) {
        if (alpha >= 0.6664) {
          this.setTooltipColor(e, 'white')
        }
      },

      cleanupMap() {
        if (!this.geoJsonLayer) return
        this.geoJsonLayer.off()
        this.geoJsonLayer.remove()

        if (!this.map) return
        this.map.off()
        this.map.remove()
      },

      getAlphaChannel(ratio) {
        const thresholds = [0.833, 0.6664, 0.4948, 0.3332, 0.1666, 0]
        const alphas = [1, 0.833, 0.6664, 0.4948, 0.3332, 0.1666, 0]

        const alpha = alphas.find((_, index) => ratio > thresholds[index])

        return alpha || 0
      },

      setFeatureStyle(e, style) {
        const layer = e.target
        layer.setStyle(style)
      },

      setTooltipColor(e, color) {
        const tooltipElement = e.target.getTooltip().getElement()
        const textElement = tooltipElement.querySelector('.interactive-map__tooltip__text')
        const valueElement = tooltipElement.querySelector('.interactive-map__tooltip__value')

        textElement.style.color = color
        valueElement.style.color = color
      },

      isIndicatorEmpty(indicatorValue) {
        return indicatorValue === null || indicatorValue === undefined
      },

      getFillColor(indicatorValue, alpha, highlighting = false) {
        if (this.isIndicatorEmpty(indicatorValue)) {
          return highlighting ? 'white' : 'white'
        }

        if (this.isSpecialIndicator()) {
          return highlighting ? '#C7C7C7' : '#ECECE2'
        }

        return highlighting ? `rgba(23, 27, 66, ${alpha})` : `rgba(152, 193, 221, ${alpha})`
      },

      resetHighlight(e) {
        this.$emit('update:hoveredRegion', null)

        this.setFeatureStyle(e, this.geoJsonLayer.resetStyle(e.target))

        this.updateOtherLayersOpacity(e, 1)
        this.setTooltipColor(e, '#172542')
        this.resetTooltipElementStyle(e)
      },

      resetTooltipElementStyle(e) {
        const tooltipElement = e.target.getTooltip().getElement()
        tooltipElement.style.borderRadius = '0'
        tooltipElement.style.background = 'transparent'
      },

      bindLayerEvents(layer) {
        layer.on({
          mouseover: this.highlightFeature,
          mouseout: this.resetHighlight
        })
      },

      createTooltipDiv(className) {
        return L.DomUtil.create('div', `interactive-map__tooltip__${className}`)
      },

      bindTooltip(feature, layer) {
        layer.bindTooltip(
          () => this.createTooltip(feature.properties.name, feature.properties.code),
          this.tooltipOptions
        )
      },

      createTooltip(regionName, regionCode) {
        const tooltipDiv = this.createTooltipDiv('')
        let indicatorValue = this.getIndicatorValueForRegion(regionCode)

        if (this.selectedCard === 'conversions') {
          indicatorValue = indicatorValue.text
        }

        let textDiv = this.createTooltipDiv('text')
        let valueDiv = this.createTooltipDiv('value')

        tooltipDiv.style.width = '110px'
        tooltipDiv.style.whiteSpace = 'normal'

        if (this.isSpecialIndicator() || this.isIndicatorEmpty(indicatorValue)) {
          textDiv = this.createTooltipDiv('value')
          valueDiv = this.createTooltipDiv('text')

          tooltipDiv.style.borderRadius = '5px'
          tooltipDiv.style.background = 'rgba(255, 255, 255, 0.30)'
          tooltipDiv.style.padding = '5px'
        }

        if (this.selectedCard === 'feeling' || this.selectedCard === 'conversions') {
          valueDiv.style.whiteSpace = 'pre'
        }

        textDiv.textContent = regionName
        valueDiv.textContent = indicatorValue || this.getEmptyValueText()

        tooltipDiv.appendChild(textDiv)
        tooltipDiv.appendChild(valueDiv)

        return tooltipDiv
      },

      onEachFeature(feature, layer) {
        this.bindLayerEvents(layer)
        this.bindTooltip(feature, layer)
      },

      createGeoJsonLayer(geoJson, map) {
        return L.geoJson(geoJson, {
          style: this.getFeatureStyle,
          onEachFeature: this.onEachFeature
        }).addTo(map)
      },

      updateGeoJsonLayer() {
        if (this.geoJsonLayer) {
          this.geoJsonLayer.clearLayers()
        }

        this.geoJsonLayer = this.createGeoJsonLayer(this.geoJson, this.map)
      },

      getMaxValue() {
        return Math.max(
          ...this.indicators
            .filter(indicator => indicator.region !== 'BR')
            .map(indicator => indicator[this.selectedCard])
        )
      },

      getFeatureStyle(feature) {
        let indicatorValue = this.getIndicatorValueForRegion(feature.properties.code, false)
        const maxValue = this.getMaxValue()

        if (this.selectedCard === 'conversions') {
          indicatorValue = indicatorValue.value
        }

        const alpha = this.getAlphaChannel(indicatorValue / maxValue)

        const fillColor = this.getFillColor(indicatorValue, alpha)

        return {
          color: '#E4E3E3',
          weight: 2,
          fillOpacity: 1,
          fillColor: fillColor
        }
      },

      initializeMap() {
        try {
          if (!this.map) {
            this.map = L.map(this.$refs.map, this.mapOptions)
          }

          this.updateGeoJsonLayer()
          this.map.fitBounds(this.geoJsonLayer.getBounds())
        } catch (error) {
          console.error('Error initializing map:', error)
        }
      },

      getEmptyValueText() {
        if (this.selectedCard === 'csat') {
          return 'Não existem avaliações nessa região.'
        }

        if (this.selectedCard === 'reason_refusal') {
          return 'Não existem recusas nessa região.'
        }

        return 'Não existem dados suficientes'
      }
    }
  }
</script>

<style lang="scss" scoped>
  .interactive-map {
    height: 500px;
    background-color: white;
  }

  /deep/ {
    .leaflet-tooltip {
      background: transparent;
      border: none;
      box-shadow: none;
      display: flex;
      justify-content: center;
      padding: 5px;
    }

    .leaflet-tooltip .interactive-map__tooltip__text {
      font-size: 12px;
      font-weight: 400;
      color: #172542;
      line-height: 16px;
    }

    .leaflet-tooltip .interactive-map__tooltip__value {
      font-size: 14px;
      font-weight: 600;
      color: #172542;
      line-height: 16px;
      // width: 100px;
      // white-space: normal;
    }
  }
</style>
